import * as THREE from 'three'

/**
 * Shader Hover Effect
 * Aplica distorsión WebGL a imágenes con clase .hover-color-offset al hacer hover.
 * Opciones configurables vía atributos data-*.
 *
 * @param {string} data-shader-effect    - "wave" | "liquid" | "chromatic" (default: "chromatic")
 * @param {string} data-shader-intensity - 0.1 a 1.0 (default: 0.5)
 * @param {string} data-shader-speed     - 0.5 a 3.0 (default: 1.0)
 */

// ─── Shaders GLSL ───────────────────────────────────────────

// Vertex shader que replica object-fit: cover via uniforms
const vertexShader = `
  uniform vec2 uUvScale;
  uniform vec2 uUvOffset;
  varying vec2 vUv;
  void main() {
    vUv = uv * uUvScale + uUvOffset;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

const fragmentShaders = {
  wave: `
    uniform sampler2D uTexture;
    uniform float uHover;
    uniform float uTime;
    uniform float uIntensity;
    varying vec2 vUv;
    void main() {
      vec2 uv = vUv;
      uv.y += sin(uv.x * 10.0 + uTime) * uIntensity * uHover;
      gl_FragColor = texture2D(uTexture, uv);
    }
  `,
  liquid: `
    uniform sampler2D uTexture;
    uniform float uHover;
    uniform float uTime;
    uniform float uIntensity;
    varying vec2 vUv;
    void main() {
      vec2 uv = vUv;
      uv += uIntensity * uHover * vec2(
        sin(uv.y * 8.0 + uTime),
        cos(uv.x * 8.0 + uTime)
      );
      gl_FragColor = texture2D(uTexture, uv);
    }
  `,
  chromatic: `
    uniform sampler2D uTexture;
    uniform float uHover;
    uniform float uTime;
    uniform float uIntensity;
    varying vec2 vUv;
    void main() {
      vec2 uv = vUv;
      float drift = sin(uTime * 0.8) * 0.3 + 0.7;
      float offset = uIntensity * uHover * 0.05 * drift;
      float r = texture2D(uTexture, uv + vec2(offset, offset * 0.5)).r;
      float g = texture2D(uTexture, uv).g;
      float b = texture2D(uTexture, uv - vec2(offset, offset * 0.5)).b;
      gl_FragColor = vec4(r, g, b, 1.0);
    }
  `
}

// ─── Shared Renderer (evita "Too many active WebGL contexts") ──

let sharedRenderer = null

function getSharedRenderer() {
  if (!sharedRenderer) {
    sharedRenderer = new THREE.WebGLRenderer({ alpha: true, antialias: true })
    sharedRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  }
  return sharedRenderer
}

// ─── Calcula UV scale/offset para replicar object-fit: cover ──

function getCoverUV(imgNaturalW, imgNaturalH, containerW, containerH) {
  const imgAspect = imgNaturalW / imgNaturalH
  const containerAspect = containerW / containerH

  let scaleX = 1, scaleY = 1, offsetX = 0, offsetY = 0

  if (imgAspect > containerAspect) {
    // Imagen más ancha que el contenedor → recortar horizontalmente
    scaleX = containerAspect / imgAspect
    offsetX = (1 - scaleX) / 2
  } else {
    // Imagen más alta que el contenedor → recortar verticalmente
    scaleY = imgAspect / containerAspect
    offsetY = (1 - scaleY) / 2
  }

  return {
    scale: new THREE.Vector2(scaleX, scaleY),
    offset: new THREE.Vector2(offsetX, offsetY)
  }
}

// ─── Clase por instancia ────────────────────────────────────

class ShaderImage {
  constructor(container) {
    this.container = container
    this.img = container.querySelector('img')
    if (!this.img) return

    this.effect = container.dataset.shaderEffect || 'chromatic'
    this.intensity = parseFloat(container.dataset.shaderIntensity) || 0.5
    this.speed = parseFloat(container.dataset.shaderSpeed) || 1.0

    this.hoverValue = { current: 0, target: 0 }
    this.startTime = performance.now()
    this.isVisible = false
    this.ready = false

    this._waitForImage()
  }

  _waitForImage() {
    // Espera a que la imagen cargue de verdad (lazy loading compatible)
    if (this.img.naturalWidth > 0) {
      this._setup()
    } else {
      this.img.addEventListener('load', () => this._setup(), { once: true })
    }
  }

  _setup() {
    const { width, height } = this.container.getBoundingClientRect()
    if (width === 0 || height === 0) return

    // Scene & Camera
    this.scene = new THREE.Scene()
    this.camera = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0.1, 10)
    this.camera.position.z = 1

    // Texture — usar currentSrc para soporte <picture>/<source>
    const loader = new THREE.TextureLoader()
    const texSrc = this.img.currentSrc || this.img.src
    this.texture = loader.load(texSrc)

    // Calcular UV para replicar object-fit: cover
    const cover = getCoverUV(this.img.naturalWidth, this.img.naturalHeight, width, height)

    // Material con shader seleccionado
    const fragCode = fragmentShaders[this.effect] || fragmentShaders.chromatic
    this.uniforms = {
      uTexture:   { value: this.texture },
      uHover:     { value: 0 },
      uTime:      { value: 0 },
      uIntensity: { value: this.intensity },
      uUvScale:   { value: cover.scale },
      uUvOffset:  { value: cover.offset }
    }

    this.material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader: fragCode,
      uniforms: this.uniforms,
      transparent: true
    })

    // Mesh
    const geometry = new THREE.PlaneGeometry(1, 1)
    this.mesh = new THREE.Mesh(geometry, this.material)
    this.scene.add(this.mesh)

    // Canvas — se superpone a la imagen
    this.canvas = document.createElement('canvas')
    this.canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:1'
    this.container.appendChild(this.canvas)

    this._resize(width, height)
    this._addEvents()
    this._observe()
    this.ready = true
  }

  _resize(w, h) {
    this.width = w
    this.height = h
    this.canvas.width = w * Math.min(window.devicePixelRatio, 2)
    this.canvas.height = h * Math.min(window.devicePixelRatio, 2)

    // Recalcular UV cover al cambiar tamaño
    if (this.img.naturalWidth > 0 && this.uniforms) {
      const cover = getCoverUV(this.img.naturalWidth, this.img.naturalHeight, w, h)
      this.uniforms.uUvScale.value = cover.scale
      this.uniforms.uUvOffset.value = cover.offset
    }
  }

  _addEvents() {
    this.container.addEventListener('mouseenter', () => {
      this.hoverValue.target = 1
    })
    this.container.addEventListener('mouseleave', () => {
      this.hoverValue.target = 0
    })

    window.addEventListener('resize', () => {
      const { width, height } = this.container.getBoundingClientRect()
      this._resize(width, height)
    })
  }

  _observe() {
    const observer = new IntersectionObserver(
      ([entry]) => {
        this.isVisible = entry.isIntersecting
        if (this.isVisible) this._animate()
      },
      { threshold: 0.1 }
    )
    observer.observe(this.container)
  }

  _animate() {
    if (!this.isVisible) return
    requestAnimationFrame(() => this._animate())

    // Lerp hover
    this.hoverValue.current += (this.hoverValue.target - this.hoverValue.current) * 0.1
    this.uniforms.uHover.value = this.hoverValue.current
    this.uniforms.uTime.value = ((performance.now() - this.startTime) / 1000) * this.speed

    // Render con Shared Renderer
    const renderer = getSharedRenderer()
    renderer.setSize(this.width, this.height)
    renderer.render(this.scene, this.camera)

    // Copiar resultado al canvas de esta instancia
    const ctx = this.canvas.getContext('2d')
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    ctx.drawImage(renderer.domElement, 0, 0, this.canvas.width, this.canvas.height)
  }
}

// ─── Init ───────────────────────────────────────────────────

export default function shaderHover() {
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches
  const isDesktopWidth = window.matchMedia('(min-width: 992px)').matches
  if (!canHover || !isDesktopWidth) return

  const containers = document.querySelectorAll('.hover-color-offset')
  if (!containers.length) return
  containers.forEach(el => new ShaderImage(el))
}
